import { Component, Injector, Input, OnInit, Signal, computed, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { GlobalSettingsFormShape } from '../../components/smart-cart-form-global-settings/global-settings.model';
import { SmartCartPreviewStateService } from '../../preview/smart-cart-preview-state.service';
import {
  CART_ITEM_IMAGE_DEFAULTS,
  CART_ITEM_IMAGE_BOUNDS,
  CART_ITEMS_STARTER_TEMPLATE,
  CART_ITEMS_VARIABLES,
  CartItemsFormShape,
} from './cart-items.model';

@Component({
  selector: 'smart-cart-form-cart-items-editor',
  templateUrl: './cart-items.component.html',
  styleUrls: ['./cart-items.component.scss'],
  standalone: false,
})
export class SmartCartFormCartItemsEditorComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<CartItemsFormShape>;
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  @Input() usageToken = '';

  readonly starterTemplate = CART_ITEMS_STARTER_TEMPLATE;
  readonly customCodeVariables = CART_ITEMS_VARIABLES;

  readonly quantityInputTypeOptions: DropdownOption[] = [
    { label: 'Controls + Manual input', value: 'arrows_manual' },
    { label: 'Dropdown selection', value: 'dropdown' },
  ];

  readonly imageFitOptions: DropdownOption[] = [
    { label: 'Crop to fill', value: 'cover' },
    { label: 'Fit whole image', value: 'contain' },
  ];

  readonly quantityPositionOptions: DropdownOption[] = [
    { label: 'Bottom of the row', value: 'bottom' },
    { label: 'Below the details', value: 'inline' },
  ];

  readonly imageBounds = CART_ITEM_IMAGE_BOUNDS;
  readonly defaultImageBackground = CART_ITEM_IMAGE_DEFAULTS.backgroundColor;

  readonly displayBackgroundColor = new FormControl<string>('', { nonNullable: true });
  readonly displayTextColor = new FormControl<string>('', { nonNullable: true });
  readonly displayHoverColor = new FormControl<string>('', { nonNullable: true });
  readonly displayIconColor = new FormControl<string>('', { nonNullable: true });
  readonly displayCornerRadius = new FormControl<number>(0, { nonNullable: true });

  backgroundColorOverridden!: Signal<boolean>;
  textColorOverridden!: Signal<boolean>;
  hoverColorOverridden!: Signal<boolean>;
  iconColorOverridden!: Signal<boolean>;
  cornerRadiusOverridden!: Signal<boolean>;

  displayTextExpanded = true;
  productImageExpanded = true;
  quantityInputExpanded = true;
  errorMessagesExpanded = false;
  subscriptionExpanded = true;
  subscriptionDesignExpanded = true;

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly previewState = inject(SmartCartPreviewStateService);

  readonly showEmptyCartPreview = this.previewState.showEmptyCart;

  ngOnInit(): void {
    const widget = this.formGroup.controls.subscription.controls.design.controls;
    const global = this.globalSettingsFormGroup.controls.buttonDesign.controls;

    this.wireDisplayString(this.displayBackgroundColor, widget.backgroundColor, global.backgroundColor);
    this.wireDisplayString(this.displayTextColor, widget.textColor, global.textColor);
    this.wireDisplayString(this.displayHoverColor, widget.hoverColor, global.hoverTextColor);
    this.wireDisplayString(this.displayIconColor, widget.iconColor, global.iconColor);
    this.wireDisplayNumber(this.displayCornerRadius, widget.cornerRadius, global.cornerRadius);

    this.backgroundColorOverridden = this.overriddenSignal(widget.backgroundColor);
    this.textColorOverridden = this.overriddenSignal(widget.textColor);
    this.hoverColorOverridden = this.overriddenSignal(widget.hoverColor);
    this.iconColorOverridden = this.overriddenSignal(widget.iconColor);
    this.cornerRadiusOverridden = this.overriddenSignal(widget.cornerRadius);
  }

  toggleDisplayText(): void {
    this.displayTextExpanded = !this.displayTextExpanded;
  }

  toggleProductImage(): void {
    this.productImageExpanded = !this.productImageExpanded;
  }

  toggleQuantityInput(): void {
    this.quantityInputExpanded = !this.quantityInputExpanded;
  }

  toggleErrorMessages(): void {
    this.errorMessagesExpanded = !this.errorMessagesExpanded;
  }

  toggleSubscription(): void {
    this.subscriptionExpanded = !this.subscriptionExpanded;
  }

  toggleSubscriptionDesign(): void {
    this.subscriptionDesignExpanded = !this.subscriptionDesignExpanded;
  }

  onEmptyCartTextChange(html: string): void {
    this.formGroup.controls.emptyCartText.setValue(html);
  }

  toggleEmptyCartPreview(): void {
    this.previewState.toggleShowEmptyCart();
  }

  resetToGlobal(widgetCtrl: AbstractControl): void {
    widgetCtrl.setValue(null);
  }

  private wireDisplayString(
    display: FormControl<string>,
    widgetCtrl: FormControl<string | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as string), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      // Empty input ↔ inherit (null), so clearing reverts to the global fallback.
      const value = next || null;
      if (widgetCtrl.value !== value) widgetCtrl.setValue(value);
    });
  }

  private wireDisplayNumber(
    display: FormControl<number>,
    widgetCtrl: FormControl<number | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as number), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      if (widgetCtrl.value !== next) widgetCtrl.setValue(next);
    });
  }

  private overriddenSignal(widgetCtrl: AbstractControl): Signal<boolean> {
    const value = toSignal(widgetCtrl.valueChanges, {
      initialValue: widgetCtrl.value,
      injector: this.injector,
    });
    return computed(() => value() !== null);
  }
}
